import * as React from 'react';
import type { PickerSize } from './picker.types';
import { cn } from '@utils';
import { ChevronDown, Text } from '@components/atoms';

type PickerTriggerProps = {
	label: string;
	icon?: React.ReactNode;
	isOpen: boolean;
	disabled?: boolean;
	size?: PickerSize;
	className?: string;
	TriggerContent?: React.ReactNode;
	RightIcon?: React.ReactNode;
};

const containerSizeMap: Record<PickerSize, string> = {
	sm: 'gap-0.5 px-1 py-1',
	md: 'gap-2 px-2 py-1',
	lg: 'gap-2 px-4 py-2',
};

const innerGapMap: Record<PickerSize, string> = {
	sm: 'gap-1',
	md: 'gap-2',
	lg: 'gap-2',
};

const chevronMarginMap: Record<PickerSize, string> = {
	sm: 'ml-0.5',
	md: 'ml-1',
	lg: 'ml-1',
};

const iconSlotSizeMap: Record<PickerSize, string> = {
	sm: 'h-3 w-3 [&_svg]:h-3 [&_svg]:w-3',
	md: 'h-5 w-5',
	lg: 'h-6 w-6',
};

const labelSizeMap: Record<PickerSize, string> = {
	sm: 'text-xs',
	md: 'text-sm',
	lg: 'text-base',
};

export const PickerTrigger: React.VoidFunctionComponent<PickerTriggerProps> = ({
	label,
	icon,
	isOpen,
	disabled,
	size = 'md',
	className,
	TriggerContent,
	RightIcon,
}) => {
	return (
		<span
			className={cn(
				'flex w-full min-w-0 items-center justify-between rounded-full border border-borderPrimary bg-white transition-colors',
				containerSizeMap[size],
				!disabled && 'hover:border-primary/40',
				disabled && 'cursor-not-allowed opacity-60',
				isOpen && 'border-2 border-primary',
				className
			)}
		>
			<span className={cn('flex min-w-0 items-center', innerGapMap[size])}>
				{icon && (
					<span className={cn('flex shrink-0 items-center justify-center text-primary', iconSlotSizeMap[size])}>
						{icon}
					</span>
				)}
				{TriggerContent || (
					<Text
						color="primary"
						weight="bold"
						className={cn('block min-w-0 truncate leading-none text-black', labelSizeMap[size])}
					>
						{label}
					</Text>
				)}
			</span>
			{RightIcon || (
				<ChevronDown className={cn('shrink-0 text-primary', iconSlotSizeMap[size], chevronMarginMap[size])} />
			)}
		</span>
	);
};
